/**
 * Lausekekielen aika- ja päivämääräfunktiot (WP-HV4, päätökset D4 = C, D5 = C).
 *
 * <p><b>Miksi nämä ovat olemassa.</b> Sidontojen lausekekielessä ei ollut
 * `Date`a eikä nykyhetkeä, joten "vanhenee 12 päivän päästä", "myöhässä" tai
 * "alkaa 3 päivän päästä" eivät olleet laskettavissa selaimessa lainkaan — ei
 * edes silloin kun kaikki tarvittava data oli jo taulussa. Rajoite siirsi työn
 * integraatioon: HR-portaalissa viisi kenttää ja tuntikirjanpidossa kolme
 * reittiä oli olemassa vain tämän takia.
 *
 * <p><b>Miksi merkkijonot eikä `Date`.</b> Kaikki funktiot ottavat ja
 * palauttavat ISO-merkkijonoja, jolloin `Date`-objekti ei tule kieleen eikä
 * `SAFE_METHODS`-porttia tarvitse avata lainkaan. Arvot ovat samassa
 * wire-formaatissa jota `date-picker` ja `date-range-picker` jo käyttävät, eli
 * tulos kelpaa suoraan takaisin kentän arvoksi.
 *
 * <p><b>D5 = C, sekoittunut aikavyöhykemalli.</b> `today()` ja `now()` ovat
 * PAIKALLISTA aikaa: väärä päivä olisi käyttäjälle heti näkyvä virhe.
 * `daysBetween` sen sijaan on puhdas päivälasku, joka lukee vain
 * päivämääräosan ja laskee UTC-keskiyöltä — "vanhenee 12 päivän päästä" ei saa
 * heilua kellonajasta eikä kesäajan vaihtumisesta.
 *
 * <p><b>Virheellinen syöte palauttaa `undefined`</b>, joka putoaa sidonnan
 * `fallback`-arvoon. Se on tarkoituksellista: `0` tai `""` näyttäisi
 * oikealta tulokselta.
 */

import { currentLocale } from './messages';

/** Pelkkä päivämääräosa: sietää sekä `YYYY-MM-DD`:n että aikaleiman. */
const DATE_PART = /^(\d{4})-(\d{2})-(\d{2})/;

const pad = (n: number): string => String(n).padStart(2, '0');

/** ISO-päivämäärä UTC-keskiyönä, tai null jos syöte ei ole päivämäärä. */
function parseDay(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const m = DATE_PART.exec(value.trim());
  if (!m) return null;
  const t = Date.parse(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`);
  if (Number.isNaN(t)) return null;
  const dt = new Date(t);
  // Date.parse hyväksyy ylivuotavat päivät (2026-02-31 → 3.3.), joten
  // varmennetaan että päivä on se joka kirjoitettiin — muuten kirjoitusvirhe
  // tuottaisi hiljaa väärän mutta uskottavan tuloksen.
  return dt.getUTCDate() === Number(m[3]) && dt.getUTCMonth() + 1 === Number(m[2])
    ? dt
    : null;
}

const fmtDay = (dt: Date): string =>
  `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;

/** Tämä päivä paikallisessa vyöhykkeessä, `YYYY-MM-DD`. */
export function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Tämä hetki paikallisessa vyöhykkeessä, `YYYY-MM-DDTHH:mm:ss`.
 *
 * <p>Muoto on sama kuin `date-picker`in `datetime`-tilan wire-formaatti (ilman
 * vyöhykepäätettä), joten arvo kelpaa suoraan kentän arvoksi. `now()` EI
 * tikitä itsestään — sidonta evaluoituu silloin kun se muutenkin evaluoituu.
 * Tikittävä lukema saadaan `clock`-komponentista (D4 = C).
 */
export function nowLocal(): string {
  const d = new Date();
  return `${todayLocal()}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Kokonaisia päiviä `a`:sta `b`:hen, etumerkillä (`b - a`).
 *
 * <p>HUOM. eri semantiikka kuin `dateRange.ts`:n samannimisellä funktiolla:
 * se on date-range-pickerin **inklusiivinen** päivälukumäärä joka rajaa
 * negatiiviset nollaan ("montako päivää tämä väli kattaa"). Tämä on
 * eksklusiivinen ja etumerkillinen ("montako päivää siihen on") — juuri se
 * mitä "vanhenee 12 päivän päästä" ja "myöhässä" tarvitsevat. Nimet ovat
 * moduulitasolla eri (`daysBetweenSigned`), jottei kumpaakaan importoi
 * vahingossa toisen paikalle.
 */
export function daysBetweenSigned(a: unknown, b: unknown): number | undefined {
  const from = parseDay(a);
  const to = parseDay(b);
  if (!from || !to) return undefined;
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

/** `date` + `n` päivää, `YYYY-MM-DD`. `n` saa olla negatiivinen. */
export function addDaysTo(date: unknown, n: unknown): string | undefined {
  const dt = parseDay(date);
  const days = typeof n === 'number' && Number.isFinite(n) ? Math.trunc(n) : null;
  if (!dt || days === null) return undefined;
  dt.setUTCDate(dt.getUTCDate() + days);
  return fmtDay(dt);
}

/** `formatDate`in tyylit. Tuntematon tyyli → `short` (ei virhe, ei tyhjä). */
export type DateStyle = 'short' | 'long' | 'weekday' | 'month';

const STYLE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { year: 'numeric', month: '2-digit', day: '2-digit' },
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  weekday: { weekday: 'long' },
  month: { year: 'numeric', month: 'long' },
};

const LOCALE_TAGS: Record<string, string> = { fi: 'fi-FI', en: 'en-GB' };

/**
 * Muotoile päivämäärä Appin kielellä. Muotoilu tehdään UTC:ssä, koska arvo on
 * päivä eikä hetki — paikallinen muotoilu siirtäisi sen naapuripäivälle
 * itäisillä vyöhykkeillä.
 */
export function formatDateLocalized(date: unknown, style?: unknown): string | undefined {
  const dt = parseDay(date);
  if (!dt) return undefined;
  const key = (typeof style === 'string' && style in STYLE_OPTIONS
    ? style
    : 'short') as DateStyle;
  const tag = LOCALE_TAGS[currentLocale()] ?? 'en-GB';
  try {
    return new Intl.DateTimeFormat(tag, { ...STYLE_OPTIONS[key], timeZone: 'UTC' }).format(dt);
  } catch {
    return fmtDay(dt);
  }
}
